import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg, fmtDate, dueClass, daysLeft } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Satellite, FileText, Upload, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const inputCls = "rounded-none bg-background border-border h-9 text-sm";
const F = ({ label, children }) => (
  <div>
    <Label className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const selectContentCls = "bg-card border-border text-foreground rounded-none";
const addBtnCls = "rounded-none bg-[#FF3B30] hover:bg-[#ff554b] font-bold uppercase tracking-widest text-xs h-9";

const DueBadge = ({ label, date: d, show = true }) => {
  if (!show) return null;
  const n = daysLeft(d);
  return (
    <div className="border border-border bg-card px-4 py-3">
      <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{label}</div>
      <div className={`font-heading font-black text-lg tracking-tighter mt-0.5 ${dueClass(d)}`}>{fmtDate(d)}</div>
      {n !== null && <div className={`text-[10px] uppercase tracking-widest font-bold ${dueClass(d)}`}>{n < 0 ? `${-n}d overdue` : `${n}d left`}</div>}
    </div>
  );
};

export default function VehicleDetailPage() {
  const { id } = useParams();
  const { canEdit } = useAuth();
  const editable = canEdit("vehicles");
  const [v, setV] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [form, setForm] = useState({});
  const [services, setServices] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [fines, setFines] = useState([]);
  const [svcOpen, setSvcOpen] = useState(false);
  const [insOpen, setInsOpen] = useState(false);
  const [svc, setSvc] = useState({ date: "", description: "", garage: "", cost: "", mileage: "", next_due: "" });
  const [ins, setIns] = useState({ type: "general", date: "", result: "pass", notes: "", next_due: "" });
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [docType, setDocType] = useState("mot_certificate");
  const [docExpiry, setDocExpiry] = useState("");
  const [fuelLogs, setFuelLogs] = useState([]);
  const [fuelOpen, setFuelOpen] = useState(false);
  const [fuelForm, setFuelForm] = useState({ date: "", cost: "", litres: "", mileage: "", station: "" });
  const [docUploading, setDocUploading] = useState(false);
  const [mot, setMot] = useState(null);
  const [motLoading, setMotLoading] = useState(false);
  const [motError, setMotError] = useState(null);

  const loadMot = async (force = false) => {
    setMotLoading(true); setMotError(null);
    try {
      const { data } = await api.get(`/vehicles/${id}/mot-history${force ? "?force=true" : ""}`);
      setMot(data);
    } catch (e) { setMotError(errMsg(e)); }
    setMotLoading(false);
  };

  const load = useCallback(() => {
    api.get(`/vehicles/${id}`).then((r) => { setV(r.data); setForm(r.data); });
    api.get(`/vehicles/${id}/services`).then((r) => setServices(r.data));
    api.get(`/vehicles/${id}/inspections`).then((r) => setInspections(r.data));
    api.get(`/fines?vehicle_id=${id}`).then((r) => setFines(r.data)).catch(() => {});
    api.get(`/vehicles/${id}/documents`).then((r) => setDocuments(r.data));
    api.get(`/vehicles/${id}/fuel`).then((r) => setFuelLogs(r.data));
    api.get("/drivers").then((r) => setDrivers(r.data)).catch(() => {});
  }, [id]);
  useEffect(load, [load]);

  if (!v) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const saveOverview = async () => {
    try {
      const payload = { ...form };
      delete payload.id; delete payload.registration; delete payload.dvla_data; delete payload.created_at;
      delete payload.driver_name; delete payload.insurance_pdf_name; delete payload.dvla_source;
      payload.year = payload.year ? Number(payload.year) : null;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const { data } = await api.put(`/vehicles/${id}`, payload);
      setV(data); setForm(data);
      toast.success("Vehicle updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const refreshDvla = async () => {
    try {
      const { data } = await api.post(`/vehicles/${id}/refresh`);
      toast.success(data.source === "dvsa" ? "Official DVSA data refreshed" : "Live vehicle data refreshed");
      if (data.safety_recall) toast.warning("This vehicle has an outstanding manufacturer safety recall");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addService = async () => {
    if (!svc.date || !svc.description) return toast.error("Date and description required");
    try {
      const payload = { ...svc, cost: svc.cost ? Number(svc.cost) : null, mileage: svc.mileage ? Number(svc.mileage) : null };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.post(`/vehicles/${id}/services`, payload);
      toast.success("Service recorded");
      setSvcOpen(false); setSvc({ date: "", description: "", garage: "", cost: "", mileage: "", next_due: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addInspection = async () => {
    if (!ins.date) return toast.error("Date required");
    try {
      const payload = { ...ins };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.post(`/vehicles/${id}/inspections`, payload);
      toast.success("Inspection recorded");
      setInsOpen(false); setIns({ type: "general", date: "", result: "pass", notes: "", next_due: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/vehicles/${id}/insurance-pdf`, fd);
      toast.success("Insurance PDF uploaded");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };

  const viewPdf = async () => {
    try {
      const res = await api.get(`/vehicles/${id}/insurance-pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
    } catch (e) { toast.error("No PDF uploaded yet"); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      if (docExpiry) fd.append("expiry_date", docExpiry);
      await api.post(`/vehicles/${id}/documents`, fd);
      toast.success("Document uploaded");
      setDocExpiry("");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setDocUploading(false); }
  };

  const viewDoc = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: d.content_type || "application/pdf" })), "_blank");
    } catch (e) { toast.error("Could not open document"); }
  };

  const deleteDoc = async (d) => {
    if (!window.confirm(`Delete document "${d.filename}"?`)) return;
    await api.delete(`/documents/${d.id}`);
    toast.success("Document deleted");
    load();
  };

  const addFuel = async () => {
    if (!fuelForm.date || !fuelForm.cost) return toast.error("Date and cost are required");
    try {
      const payload = { ...fuelForm, cost: Number(fuelForm.cost), litres: fuelForm.litres ? Number(fuelForm.litres) : null, mileage: fuelForm.mileage ? Number(fuelForm.mileage) : null };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.post(`/vehicles/${id}/fuel`, payload);
      toast.success("Fuel fill-up logged");
      setFuelOpen(false);
      setFuelForm({ date: "", cost: "", litres: "", mileage: "", station: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const DOC_LABEL = { mot_certificate: "MOT certificate", service_invoice: "Service invoice", insurance: "Insurance", other: "Other" };

  const INSPECTION_LABEL = { general: "General", tail_lift: "Tail Lift", fridge: "Fridge" };
  const RESULT_STYLE = { pass: "text-emerald-600 dark:text-[#32D74B]", fail: "text-[#FF3B30]", advisory: "text-amber-600 dark:text-[#FFD60A]" };

  return (
    <div className="p-8" data-testid="vehicle-detail-page">
      <Link to="/vehicles" data-testid="back-to-vehicles-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={14} /> Vehicles
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="inline-block bg-[#FFD60A] text-black font-mono font-black text-2xl px-4 py-1.5 tracking-wider" data-testid="vehicle-reg-plate">{v.registration}</div>
          <div className="text-muted-foreground mt-2 text-sm">
            {[v.make, v.model, v.year].filter(Boolean).join(" · ") || "No details"} {v.driver_name && <>· Driver: <span className="text-foreground font-semibold">{v.driver_name}</span></>}
          </div>
        </div>
        {editable && (
        <Button data-testid="refresh-dvla-button" onClick={refreshDvla} variant="outline" className="rounded-none border-amber-600/50 dark:border-[#FFD60A]/50 text-amber-600 dark:text-[#FFD60A] hover:bg-amber-600/10 dark:hover:bg-amber-600/10 dark:bg-[#FFD60A]/10 hover:text-amber-600 dark:hover:text-amber-600 dark:text-[#FFD60A] uppercase text-xs tracking-widest font-bold bg-transparent h-9">
          <Satellite size={14} className="mr-1.5" /> Refresh DVLA
        </Button>
        )}
      </div>

      {v.safety_recall && (
        <div data-testid="safety-recall-banner" className="border border-[#FF3B30] bg-[#FF3B30]/10 px-4 py-3 mb-4 flex items-center gap-3">
          <AlertTriangle size={18} className="text-[#FF3B30] shrink-0" />
          <div>
            <div className="uppercase text-xs tracking-[0.2em] font-black text-[#FF3B30]">Outstanding safety recall</div>
            <div className="text-sm text-foreground/80 mt-0.5">DVSA reports a manufacturer safety recall on this vehicle. Contact your dealer to book the free repair.</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
        <DueBadge label="MOT" date={v.mot_expiry} />
        <DueBadge label="Tax" date={v.tax_due} />
        <DueBadge label="Service" date={v.service_due} />
        <DueBadge label="Inspection" date={v.inspection_due} />
        <DueBadge label="Tail lift" date={v.tail_lift_expiry} show={v.has_tail_lift} />
        <DueBadge label="Fridge" date={v.fridge_expiry} show={v.has_fridge} />
        <DueBadge label="Insurance" date={v.insurance_expiry} />
      </div>

      <Tabs defaultValue="overview" onValueChange={(val) => { if (val === "mot" && !mot && !motLoading) loadMot(); }}>
        <TabsList className="rounded-none bg-card border border-border h-11 p-1">
          {["overview", "mot", "services", "inspections", "fuel", "fines", "insurance", "documents"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="rounded-none uppercase text-xs tracking-widest font-bold data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white px-5">
              {t === "mot" ? "MOT history" : t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="border border-border bg-card p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <F label="Make"><Input value={form.make || ""} onChange={(e) => set("make", e.target.value)} className={inputCls} /></F>
              <F label="Model"><Input value={form.model || ""} onChange={(e) => set("model", e.target.value)} className={inputCls} /></F>
              <F label="Colour"><Input value={form.colour || ""} onChange={(e) => set("colour", e.target.value)} className={inputCls} /></F>
              <F label="Fuel"><Input value={form.fuel_type || ""} onChange={(e) => set("fuel_type", e.target.value)} className={inputCls} /></F>
              <F label="Year"><Input type="number" value={form.year || ""} onChange={(e) => set("year", e.target.value)} className={inputCls} /></F>
              <F label="Tyre size front"><Input data-testid="detail-tyre-front-input" value={form.tyre_size_front || ""} onChange={(e) => set("tyre_size_front", e.target.value)} className={inputCls} /></F>
              <F label="Tyre size rear"><Input data-testid="detail-tyre-rear-input" value={form.tyre_size_rear || ""} onChange={(e) => set("tyre_size_rear", e.target.value)} className={inputCls} /></F>
              <F label="Driver">
                <Select value={form.driver_id || "none"} onValueChange={(val) => set("driver_id", val === "none" ? null : val)}>
                  <SelectTrigger data-testid="detail-driver-select" className={inputCls}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="MOT expiry"><Input type="date" value={form.mot_expiry || ""} onChange={(e) => set("mot_expiry", e.target.value)} className={inputCls} /></F>
              <F label="Tax due"><Input type="date" value={form.tax_due || ""} onChange={(e) => set("tax_due", e.target.value)} className={inputCls} /></F>
              <F label="Service due"><Input type="date" value={form.service_due || ""} onChange={(e) => set("service_due", e.target.value)} className={inputCls} /></F>
              <F label="Inspection due"><Input type="date" value={form.inspection_due || ""} onChange={(e) => set("inspection_due", e.target.value)} className={inputCls} /></F>
              <div className="flex items-center justify-between border border-border px-3 h-9 self-end">
                <span className="text-xs text-foreground/80">Tail lift</span>
                <Switch checked={!!form.has_tail_lift} onCheckedChange={(val) => set("has_tail_lift", val)} />
              </div>
              <div className="flex items-center justify-between border border-border px-3 h-9 self-end">
                <span className="text-xs text-foreground/80">Fridge</span>
                <Switch checked={!!form.has_fridge} onCheckedChange={(val) => set("has_fridge", val)} />
              </div>
              {form.has_tail_lift && <F label="Tail lift due"><Input type="date" value={form.tail_lift_expiry || ""} onChange={(e) => set("tail_lift_expiry", e.target.value)} className={inputCls} /></F>}
              {form.has_fridge && <F label="Fridge due"><Input type="date" value={form.fridge_expiry || ""} onChange={(e) => set("fridge_expiry", e.target.value)} className={inputCls} /></F>}
            </div>
            {editable && <Button data-testid="save-overview-button" onClick={saveOverview} className={`${addBtnCls} mt-6 h-10 px-8`}>Save changes</Button>}
          </div>
          {v.dvla_data && (
            <div className="border border-border bg-card mt-4 p-6" data-testid="dvla-data-panel">
              <div className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground mb-3">
                DVLA record {v.dvla_source === "mock" && <span className="text-amber-600 dark:text-[#FFD60A]">(MOCK — lookup was unavailable)</span>}{v.dvla_source === "dvsa" && <span className="text-emerald-600 dark:text-[#32D74B]">(OFFICIAL — DVSA MOT History API)</span>}{(v.dvla_source === "web" || v.dvla_source === "govuk") && <span className="text-emerald-600 dark:text-[#32D74B]">(LIVE — public vehicle lookup)</span>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                {Object.entries(v.dvla_data).filter(([k]) => !["source", "note"].includes(k)).map(([k, val]) => (
                  <div key={k} className="flex justify-between border-b border-border/60 py-1">
                    <span className="text-muted-foreground text-xs">{k}</span>
                    <span className="font-mono text-xs">{String(val)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="mot" className="mt-4">
          <div className="border border-border bg-card" data-testid="mot-history-panel">
            <div className="px-5 py-3 border-b border-border flex justify-between items-center">
              <span className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground">MOT test history — official DVSA record</span>
              <Button data-testid="mot-history-refresh-button" onClick={() => loadMot(true)} disabled={motLoading} variant="outline" className="rounded-none border-border uppercase text-[10px] tracking-widest font-bold h-8 px-3 bg-transparent">
                <Satellite size={12} className="mr-1.5" /> Refresh
              </Button>
            </div>
            {motLoading && <div className="px-5 py-10 text-center text-muted-foreground text-sm" data-testid="mot-history-loading">Fetching DVSA record…</div>}
            {motError && <div className="px-5 py-6 text-sm text-[#FF3B30]" data-testid="mot-history-error">{motError}</div>}
            {!motLoading && mot && mot.tests.length === 0 && (
              <div className="px-5 py-10 text-center text-muted-foreground/70 text-sm" data-testid="mot-history-empty">
                No MOT tests recorded yet{mot.mot_due ? ` — first MOT due ${fmtDate(mot.mot_due)}` : ""}.
              </div>
            )}
            {!motLoading && mot && mot.tests.map((t, i) => {
              const passed = t.result === "PASSED";
              return (
                <div key={i} className="px-5 py-4 border-b border-border/60" data-testid={`mot-test-${i}`}>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${passed ? "bg-emerald-600/15 text-emerald-600 dark:text-[#32D74B]" : "bg-[#FF3B30]/15 text-[#FF3B30]"}`}>{t.result}</span>
                    <span className="font-mono font-bold text-sm">{fmtDate(t.date)}</span>
                    {t.odometer && <span className="text-xs text-muted-foreground font-mono">{t.odometer}</span>}
                    {t.expiry && <span className="text-xs text-muted-foreground">Expiry: <span className="font-mono">{fmtDate(t.expiry)}</span></span>}
                    {t.test_number && <span className="text-[10px] text-muted-foreground/60 font-mono ml-auto">#{t.test_number}</span>}
                  </div>
                  {t.defects.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {t.defects.map((d, j) => {
                        const dt = (d.type || "").toUpperCase();
                        const cls = d.dangerous || dt === "DANGEROUS" ? "bg-[#FF3B30] text-white" : dt === "MAJOR" || dt === "FAIL" ? "bg-[#FF3B30]/15 text-[#FF3B30]" : dt === "ADVISORY" || dt === "USER ENTERED" ? "bg-muted text-muted-foreground" : "bg-amber-500/15 text-amber-600 dark:text-[#FFD60A]";
                        return (
                          <div key={j} className="flex gap-2 items-start text-xs">
                            <span className={`px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest shrink-0 ${cls}`}>{dt || "NOTE"}</span>
                            <span className="text-foreground/80">{d.text}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="services" className="mt-4">
          <div className="border border-border bg-card">
            <div className="px-5 py-3 border-b border-border flex justify-between items-center">
              <span className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground">Service history</span>
              <Dialog open={svcOpen} onOpenChange={setSvcOpen}>
                {editable && <DialogTrigger asChild><Button data-testid="add-service-button" className={addBtnCls}><Plus size={14} className="mr-1" /> Add service</Button></DialogTrigger>}
                <DialogContent className="bg-card border-border rounded-none text-foreground">
                  <DialogHeader><DialogTitle className="font-heading font-black tracking-tighter text-2xl">RECORD SERVICE</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Date *"><Input data-testid="service-date-input" type="date" value={svc.date} onChange={(e) => setSvc((s) => ({ ...s, date: e.target.value }))} className={inputCls} /></F>
                    <F label="Garage"><Input data-testid="service-garage-input" value={svc.garage} onChange={(e) => setSvc((s) => ({ ...s, garage: e.target.value }))} className={inputCls} /></F>
                    <F label="Cost (£)"><Input type="number" value={svc.cost} onChange={(e) => setSvc((s) => ({ ...s, cost: e.target.value }))} className={inputCls} /></F>
                    <F label="Mileage"><Input type="number" value={svc.mileage} onChange={(e) => setSvc((s) => ({ ...s, mileage: e.target.value }))} className={inputCls} /></F>
                    <F label="Next service due"><Input type="date" value={svc.next_due} onChange={(e) => setSvc((s) => ({ ...s, next_due: e.target.value }))} className={inputCls} /></F>
                    <div className="col-span-2"><F label="Description *"><Input data-testid="service-description-input" value={svc.description} onChange={(e) => setSvc((s) => ({ ...s, description: e.target.value }))} className={inputCls} /></F></div>
                  </div>
                  <Button data-testid="save-service-button" onClick={addService} className={`${addBtnCls} w-full h-11`}>Save service</Button>
                </DialogContent>
              </Dialog>
            </div>
            <table className="w-full text-sm" data-testid="services-table">
              <thead><tr className="text-left border-b border-border">{["Date", "Description", "Garage", "Cost", "Mileage", ""].map((h, i) => <th key={i} className="px-4 py-2.5 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{h}</th>)}</tr></thead>
              <tbody>
                {services.map((s) => (
                  <tr key={s.id} className="border-b border-border/60 hover:bg-muted/60">
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(s.date)}</td>
                    <td className="px-4 py-2.5">{s.description}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{s.garage || "—"}</td>
                    <td className="px-4 py-2.5">{s.cost != null ? `£${s.cost.toFixed(2)}` : "—"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{s.mileage?.toLocaleString() || "—"}</td>
                    <td className="px-4 py-2.5">{editable && <button onClick={async () => { await api.delete(`/services/${s.id}`); load(); }} className="text-muted-foreground/70 hover:text-[#FF3B30]"><Trash2 size={14} /></button>}</td>
                  </tr>
                ))}
                {services.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground/70">No services recorded.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="inspections" className="mt-4">
          <div className="border border-border bg-card">
            <div className="px-5 py-3 border-b border-border flex justify-between items-center">
              <span className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground">Inspections (general / tail lift / fridge)</span>
              <Dialog open={insOpen} onOpenChange={setInsOpen}>
                {editable && <DialogTrigger asChild><Button data-testid="add-inspection-button" className={addBtnCls}><Plus size={14} className="mr-1" /> Add inspection</Button></DialogTrigger>}
                <DialogContent className="bg-card border-border rounded-none text-foreground">
                  <DialogHeader><DialogTitle className="font-heading font-black tracking-tighter text-2xl">RECORD INSPECTION</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Type *">
                      <Select value={ins.type} onValueChange={(val) => setIns((s) => ({ ...s, type: val }))}>
                        <SelectTrigger data-testid="inspection-type-select" className={inputCls}><SelectValue /></SelectTrigger>
                        <SelectContent className={selectContentCls}>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="tail_lift">Tail lift</SelectItem>
                          <SelectItem value="fridge">Fridge</SelectItem>
                        </SelectContent>
                      </Select>
                    </F>
                    <F label="Date *"><Input data-testid="inspection-date-input" type="date" value={ins.date} onChange={(e) => setIns((s) => ({ ...s, date: e.target.value }))} className={inputCls} /></F>
                    <F label="Result">
                      <Select value={ins.result} onValueChange={(val) => setIns((s) => ({ ...s, result: val }))}>
                        <SelectTrigger data-testid="inspection-result-select" className={inputCls}><SelectValue /></SelectTrigger>
                        <SelectContent className={selectContentCls}>
                          <SelectItem value="pass">Pass</SelectItem>
                          <SelectItem value="fail">Fail</SelectItem>
                          <SelectItem value="advisory">Advisory</SelectItem>
                        </SelectContent>
                      </Select>
                    </F>
                    <F label="Next due (updates reminder)"><Input data-testid="inspection-nextdue-input" type="date" value={ins.next_due} onChange={(e) => setIns((s) => ({ ...s, next_due: e.target.value }))} className={inputCls} /></F>
                    <div className="col-span-2"><F label="Notes"><Input value={ins.notes} onChange={(e) => setIns((s) => ({ ...s, notes: e.target.value }))} className={inputCls} /></F></div>
                  </div>
                  <Button data-testid="save-inspection-button" onClick={addInspection} className={`${addBtnCls} w-full h-11`}>Save inspection</Button>
                </DialogContent>
              </Dialog>
            </div>
            <table className="w-full text-sm" data-testid="inspections-table">
              <thead><tr className="text-left border-b border-border">{["Date", "Type", "Result", "Next due", "Notes", ""].map((h, i) => <th key={i} className="px-4 py-2.5 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{h}</th>)}</tr></thead>
              <tbody>
                {inspections.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 hover:bg-muted/60">
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-4 py-2.5">{INSPECTION_LABEL[r.type]}</td>
                    <td className={`px-4 py-2.5 uppercase text-xs font-bold ${RESULT_STYLE[r.result]}`}>{r.result}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{fmtDate(r.next_due)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.notes || "—"}</td>
                    <td className="px-4 py-2.5">{editable && <button onClick={async () => { await api.delete(`/inspections/${r.id}`); load(); }} className="text-muted-foreground/70 hover:text-[#FF3B30]"><Trash2 size={14} /></button>}</td>
                  </tr>
                ))}
                {inspections.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground/70">No inspections recorded.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="fuel" className="mt-4">
          <div className="border border-border bg-card">
            <div className="px-5 py-3 border-b border-border flex justify-between items-center">
              <span className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground">Fuel fill-ups</span>
              <Dialog open={fuelOpen} onOpenChange={setFuelOpen}>
                {editable && <DialogTrigger asChild><Button data-testid="add-fuel-button" className={addBtnCls}><Plus size={14} className="mr-1" /> Log fuel</Button></DialogTrigger>}
                <DialogContent className="bg-card border-border rounded-none text-foreground">
                  <DialogHeader><DialogTitle className="font-heading font-black tracking-tighter text-2xl">LOG FUEL FILL-UP</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <F label="Date *"><Input data-testid="fuel-date-input" type="date" value={fuelForm.date} onChange={(e) => setFuelForm((s) => ({ ...s, date: e.target.value }))} className={inputCls} /></F>
                    <F label="Cost (£) *"><Input data-testid="fuel-cost-input" type="number" value={fuelForm.cost} onChange={(e) => setFuelForm((s) => ({ ...s, cost: e.target.value }))} className={inputCls} /></F>
                    <F label="Litres"><Input data-testid="fuel-litres-input" type="number" value={fuelForm.litres} onChange={(e) => setFuelForm((s) => ({ ...s, litres: e.target.value }))} className={inputCls} /></F>
                    <F label="Mileage"><Input type="number" value={fuelForm.mileage} onChange={(e) => setFuelForm((s) => ({ ...s, mileage: e.target.value }))} className={inputCls} /></F>
                    <div className="col-span-2"><F label="Station"><Input data-testid="fuel-station-input" value={fuelForm.station} onChange={(e) => setFuelForm((s) => ({ ...s, station: e.target.value }))} className={inputCls} /></F></div>
                  </div>
                  <Button data-testid="save-fuel-button" onClick={addFuel} className={`${addBtnCls} w-full h-11`}>Save fill-up</Button>
                </DialogContent>
              </Dialog>
            </div>
            <table className="w-full text-sm" data-testid="fuel-table">
              <thead><tr className="text-left border-b border-border">{["Date", "Cost", "Litres", "£/litre", "Mileage", "Station", ""].map((h, i) => <th key={i} className="px-4 py-2.5 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{h}</th>)}</tr></thead>
              <tbody>
                {fuelLogs.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 hover:bg-muted/60">
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-4 py-2.5 font-bold">£{r.cost.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.litres ?? "—"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.litres ? `£${(r.cost / r.litres).toFixed(2)}` : "—"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.mileage?.toLocaleString() || "—"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.station || "—"}</td>
                    <td className="px-4 py-2.5">{editable && <button onClick={async () => { await api.delete(`/fuel/${r.id}`); load(); }} className="text-muted-foreground/70 hover:text-[#FF3B30]"><Trash2 size={14} /></button>}</td>
                  </tr>
                ))}
                {fuelLogs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground/70">No fuel logged yet. Fuel spend feeds into the Costs page.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="fines" className="mt-4">
          <div className="border border-border bg-card">
            <div className="px-5 py-3 border-b border-border flex justify-between items-center">
              <span className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground">PCN fines for this vehicle</span>
              <Link to="/fines" className="text-xs text-[#FF3B30] hover:underline">Manage on fines page →</Link>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left border-b border-border">{["Reference", "Date", "Driver", "Company", "Amount", "Reason", "Status"].map((h, i) => <th key={i} className="px-4 py-2.5 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{h}</th>)}</tr></thead>
              <tbody>
                {fines.map((f) => (
                  <tr key={f.id} className="border-b border-border/60 hover:bg-muted/60">
                    <td className="px-4 py-2.5 font-mono font-bold">{f.reference}</td>
                    <td className="px-4 py-2.5">{fmtDate(f.date)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{f.driver_name || "—"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{f.issuer || "—"}</td>
                    <td className="px-4 py-2.5 font-bold">£{f.amount.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{f.reason || "—"}</td>
                    <td className={`px-4 py-2.5 uppercase text-xs font-bold ${f.status === "paid" ? "text-emerald-600 dark:text-[#32D74B]" : f.status === "appealed" ? "text-amber-600 dark:text-[#FFD60A]" : "text-[#FF3B30]"}`}>{f.status}</td>
                  </tr>
                ))}
                {fines.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground/70">No fines for this vehicle.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="insurance" className="mt-4">
          <div className="border border-border bg-card p-6 max-w-2xl">
            <div className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground mb-4">Insurance details</div>
            <div className="grid grid-cols-2 gap-4">
              <F label="Insurance company"><Input data-testid="insurance-company-input" value={form.insurance_company || ""} onChange={(e) => set("insurance_company", e.target.value)} className={inputCls} /></F>
              <F label="Policy number"><Input data-testid="insurance-policy-input" value={form.insurance_policy_number || ""} onChange={(e) => set("insurance_policy_number", e.target.value)} className={inputCls} /></F>
              <F label="Expiry date"><Input data-testid="insurance-expiry-input" type="date" value={form.insurance_expiry || ""} onChange={(e) => set("insurance_expiry", e.target.value)} className={inputCls} /></F>
            </div>
            {editable && <Button data-testid="save-insurance-button" onClick={saveOverview} className={`${addBtnCls} mt-5 h-10 px-8`}>Save insurance details</Button>}
            <div className="mt-6 pt-5 border-t border-border">
              <div className="uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground mb-3">Insurance document (PDF)</div>
              {v.insurance_pdf_name && (
                <button data-testid="view-insurance-pdf-button" onClick={viewPdf} className="flex items-center gap-2 text-sm text-emerald-600 dark:text-[#32D74B] hover:underline mb-3">
                  <FileText size={15} /> {v.insurance_pdf_name} — view
                </button>
              )}
              {editable && (
              <label className="inline-flex items-center gap-2 border border-border px-4 py-2.5 text-xs uppercase tracking-widest font-bold cursor-pointer hover:bg-muted transition-colors duration-150">
                <Upload size={14} /> {uploading ? "Uploading…" : v.insurance_pdf_name ? "Replace PDF" : "Upload PDF"}
                <input data-testid="insurance-pdf-input" type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadPdf(e.target.files?.[0])} />
              </label>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <div className="border border-border bg-card">
            <div className="px-5 py-3 border-b border-border flex justify-between items-center flex-wrap gap-3">
              <span className="uppercase text-xs tracking-[0.2em] font-bold text-muted-foreground">Documents (MOT certificates, service invoices, etc.)</span>
              {editable && (
              <div className="flex items-center gap-2">
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger data-testid="document-type-select" className={`${inputCls} w-44`}><SelectValue /></SelectTrigger>
                  <SelectContent className={selectContentCls}>
                    <SelectItem value="mot_certificate">MOT certificate</SelectItem>
                    <SelectItem value="service_invoice">Service invoice</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Input data-testid="document-expiry-input" type="date" value={docExpiry} onChange={(e) => setDocExpiry(e.target.value)} title="Expiry date (optional, adds a reminder)" className={`${inputCls} w-40`} />
                <label className="inline-flex items-center gap-2 bg-[#FF3B30] hover:bg-[#ff554b] text-white px-4 h-9 text-xs uppercase tracking-widest font-bold cursor-pointer transition-colors duration-150">
                  <Upload size={14} /> {docUploading ? "Uploading…" : "Upload"}
                  <input data-testid="document-file-input" type="file" accept="application/pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { uploadDoc(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              </div>
              )}
            </div>
            <table className="w-full text-sm" data-testid="documents-table">
              <thead><tr className="text-left border-b border-border">{["File", "Type", "Uploaded", "Expiry", "", ""].map((h, i) => <th key={i} className="px-4 py-2.5 uppercase text-[10px] tracking-[0.2em] font-bold text-muted-foreground">{h}</th>)}</tr></thead>
              <tbody>
                {documents.map((d) => (
                  <tr key={d.id} className="border-b border-border/60 hover:bg-muted/60">
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-2 font-semibold"><FileText size={15} className="text-muted-foreground" /> {d.filename}</span>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{DOC_LABEL[d.doc_type] || d.doc_type}</td>
                    <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString("en-GB") : "—"}</td>
                    <td className={`px-4 py-2.5 whitespace-nowrap ${dueClass(d.expiry_date)}`}>{fmtDate(d.expiry_date)}</td>
                    <td className="px-4 py-2.5">
                      <button data-testid={`view-document-${d.filename}`} onClick={() => viewDoc(d)} className="text-[10px] uppercase tracking-widest font-bold text-emerald-600 dark:text-[#32D74B] hover:underline">View</button>
                    </td>
                    <td className="px-4 py-2.5">
                      {editable && (
                      <button data-testid={`delete-document-${d.filename}`} onClick={() => deleteDoc(d)} className="text-muted-foreground/70 hover:text-[#FF3B30] transition-colors duration-150"><Trash2 size={14} /></button>
                      )}
                    </td>
                  </tr>
                ))}
                {documents.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground/70">No documents yet. Pick a type and upload a PDF or photo.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
